import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth, afterLogin } from "@/context/AuthContext";
import { Brand } from "@/components/AppShell";
import { Mail, Lock, User, Eye, EyeOff, ArrowRight } from "lucide-react";

const Field = ({ icon: Icon, children }) => (
  <div className="relative">
    <Icon className="absolute left-4 top-1/2 -translate-y-1/2 text-white/35" size={17} />
    {children}
  </div>
);

export default function Login() {
  const invited = localStorage.getItem("mulankan_ref");
  const [mode, setMode] = useState(invited ? "register" : "login");
  const [form, setForm] = useState({ name: "", email: "", password: "", confirm: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [show, setShow] = useState(false);
  const [notice, setNotice] = useState("");
  const { applyAuth } = useAuth();
  const navigate = useNavigate();
  const set = (k) => (e) => { const v = e.target.value; setForm((f) => ({ ...f, [k]: v })); };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setNotice("");
    if (mode === "reset" && form.password !== form.confirm) { setError("Passwords don't match"); return; }
    setLoading(true);
    try {
      if (mode === "reset") {
        const { data } = await api.post("/auth/reset-password", { email: form.email, new_password: form.password });
        setForm({ ...form, password: "", confirm: "" });
        setMode("login");
        setNotice(data.message);
        return;
      }
      const login = mode === "login";
      const { data } = await api.post(login ? "/auth/login" : "/auth/register",
        login ? { email: form.email, password: form.password } : { ...form, ref: invited || undefined });
      if (!login) localStorage.removeItem("mulankan_ref");
      applyAuth(data);
      navigate(afterLogin());
    } catch (err) {
      setError(apiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="flow-bg min-h-screen flex items-center justify-center px-4 py-10" data-testid="login-page">
      <div className="panel w-full max-w-md p-8 fade-up">
        <div className="mb-8"><Brand /></div>
        <h1 className="font-serif text-4xl text-[var(--ink)] mb-6">{mode === "login" ? "Welcome back" : mode === "reset" ? "Set a new password" : "Create your account"}</h1>
        {mode === "reset" ? (
          <button onClick={() => { setMode("login"); setError(""); }} data-testid="back-to-login" className="text-xs text-white/50 hover:text-[var(--accent)] mb-5 transition-colors">← Back to log in</button>
        ) : (
        <div className="flex rounded-full bg-white/[0.04] p-1 mb-6">
          {["login", "register"].map((m) => (
            <button key={m} onClick={() => { setMode(m); setError(""); }} data-testid={`tab-${m}`}
              className={`flex-1 py-2 rounded-full text-sm font-semibold transition-colors ${mode === m ? "bg-[var(--ink)] text-black" : "text-white/50"}`}>
              {m === "login" ? "Log in" : "Sign up"}
            </button>
          ))}
        </div>
        )}
        {notice && <p className="mb-4 rounded-xl border border-emerald-400/30 bg-emerald-400/10 px-4 py-2.5 text-xs text-emerald-100" data-testid="auth-notice">{notice}</p>}
        {invited && mode === "register" && (
          <p className="mb-4 rounded-xl border border-[var(--accent)]/30 bg-[var(--accent)]/10 px-4 py-2.5 text-xs text-[var(--ink)]" data-testid="invite-bonus-note">
            You were invited — sign up to get 5 bonus tasks (10 free tasks in total).
          </p>
        )}
        <form onSubmit={submit} className="space-y-3" data-testid="auth-form">
          {mode === "register" && (
            <Field icon={User}><input required type="text" autoComplete="name" autoCapitalize="words" enterKeyHint="next" placeholder="Your name" value={form.name} onChange={set("name")} data-testid="input-name" className="field" style={{ paddingLeft: 44 }} /></Field>
          )}
          <Field icon={Mail}><input required type="email" inputMode="email" autoComplete="email" autoCapitalize="none" autoCorrect="off" spellCheck={false} enterKeyHint="next" placeholder="Email" value={form.email} onChange={set("email")} data-testid="input-email" className="field" style={{ paddingLeft: 44 }} /></Field>
          <Field icon={Lock}>
            <input required minLength={mode === "login" ? undefined : 6} type={show ? "text" : "password"} autoComplete={mode === "login" ? "current-password" : "new-password"} autoCapitalize="none" autoCorrect="off" spellCheck={false} placeholder={mode === "reset" ? "New password (min 6 characters)" : "Password"} value={form.password} onChange={set("password")} data-testid="input-password" className="field" style={{ paddingLeft: 44, paddingRight: 44 }} />
            <button type="button" onClick={() => setShow((v) => !v)} data-testid="toggle-password-visibility" aria-label={show ? "Hide password" : "Show password"}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-white/40 hover:text-white transition-colors">{show ? <EyeOff size={17} /> : <Eye size={17} />}</button>
          </Field>
          {mode === "reset" && (
            <Field icon={Lock}><input required type={show ? "text" : "password"} autoComplete="new-password" autoCapitalize="none" autoCorrect="off" spellCheck={false} placeholder="Confirm new password" value={form.confirm} onChange={set("confirm")} data-testid="input-confirm-password" className="field" style={{ paddingLeft: 44 }} /></Field>
          )}
          {mode === "login" && (
            <button type="button" onClick={() => { setMode("reset"); setError(""); setNotice(""); }} data-testid="forgot-password-link" className="text-xs text-white/50 hover:text-[var(--accent)] transition-colors">Forgot password?</button>
          )}
          {error && <p className="text-rose-300 text-sm" data-testid="auth-error">{error}</p>}
          <button type="submit" disabled={loading} data-testid="submit-auth-button" className="btn-accent w-full py-3">
            {loading ? "…" : mode === "login" ? "Log in" : mode === "reset" ? "Change password" : "Create account"} <ArrowRight size={16} />
          </button>
        </form>
        {mode !== "reset" && <>
        <div className="flex items-center gap-3 my-5"><div className="flex-1 h-px bg-white/10" /><span className="text-xs text-white/35">or</span><div className="flex-1 h-px bg-white/10" /></div>
        <button onClick={googleLogin} data-testid="google-login-button"
          className="w-full py-3 rounded-full bg-white text-slate-800 font-semibold flex items-center justify-center gap-2 hover:bg-slate-100 transition-colors">
          <img src="https://www.svgrepo.com/show/475656/google-color.svg" alt="" className="w-5 h-5" /> Continue with Google
        </button>
        </>}
      </div>
    </div>
  );
}
